import { computed, defineComponent, inject, onMounted, ref } from 'vue';
import SHeaderSection from '@/components/stemdo-components/s-header-section/SHeaderSection.vue';
import TeamRequestService from '@/sections/admin/entities/team-request/team-request.service';
import TeamRequestOverview from './team-request-overview/TeamRequestOverview.vue';
import ButtonsGroup from './buttons-group/ButtonsGroup.vue';
import HeaderTeamRequestStatus from './header-team-request-status/HeaderTeamRequestStatus.vue';
import StemdoerList from './stemdoer-list/StemdoerList.vue';
import { useRouter } from '@/plugins/router';
import { TeamRequest } from '@/shared/model/team-request.model';
import { IStemdoerRate, StemdoerRate, StemdoerRates } from '@/shared/model/stemdoer-rate.model';
import store from '@/plugins/vuex';
import SPopUp from '@/components/stemdo-components/s-pop-up/SPopUp.vue';
import { ExtendedTeamRequestStatus } from './enum/extendedTeamRequestStatus';
import { RequestStatus } from '@/shared/model/enumerations/request-status.model';
import SShareLink from '@/components/stemdo-components/s-share-link/SShareLink.vue';
import CompanyService from '@/sections/admin/entities/company/company.service';

export default defineComponent({
  props: {
    teamRequestId: {
      type: String,
      required: true
    }
  },
  components: {
    SHeaderSection,
    TeamRequestOverview,
    ButtonsGroup,
    HeaderTeamRequestStatus,
    StemdoerList,
    SPopUp,
    SShareLink
  },
  setup(props) {
    const router = useRouter();
    const isUserManager = computed(() => store.getters.account?.authorities.includes('ROLE_GESTOR') ?? false);
    const isUserClient = computed(() => store.getters.account?.authorities.includes('ROLE_CLIENT') ?? false);
    const isAvailableStemdoersModalOpen = ref(false);
    const existStemdoerNotAvailable = ref(false);
    const teamRequestService: TeamRequestService = inject('teamRequestService');
    const companyService = inject('companyService') as CompanyService;
    const teamRequest = ref<TeamRequest>(TeamRequest.empty());
    const originalStemdoerRates = ref<IStemdoerRate[]>([]);
    const errorPopUp = ref(false);
    const successPopUp = ref(false);
    const successMessage = ref('');
    const sPopUpData = ref({
      open: false,
      text: ''
    });
    const isAnyRateChanged = computed(() => {
      return teamRequest.value.stemdoerRates?.some(sr => {
        const originalRate = originalStemdoerRates.value.find(osr => osr.stemdoer.id === sr.stemdoer.id)?.rate;
        return sr.rate !== originalRate;
      });
    });
    const areStemdoersChanged = ref(false);
    const extendedTeamRequestStatus = computed(() => {
      const { status, modifiedByManager, areAllStemdoersAvailable } = teamRequest.value;
      const userModified = isAnyRateChanged.value || areStemdoersChanged.value;
      if (isUserManager.value) {
        if (status === RequestStatus.PENDING) {
          return userModified
            ? ExtendedTeamRequestStatus.MANAGER_AND_PENDING_AND_USER_MODIFIED
            : ExtendedTeamRequestStatus.MANAGER_AND_PENDING_AND_USER_NOT_MODIFIED;
        }
        if (status === RequestStatus.VALIDATED) {
          if (!areAllStemdoersAvailable()) return ExtendedTeamRequestStatus.MANAGER_AND_VALIDATED_AND_NOT_ALL_STEMDOERS_AVAILABLE;
          return userModified
            ? ExtendedTeamRequestStatus.MANAGER_AND_VALIDATED_AND_USER_MODIFIED
            : ExtendedTeamRequestStatus.MANAGER_AND_VALIDATED_AND_USER_NOT_MODIFIED;
        }
      }
      if (isUserClient.value) {
        if (status === RequestStatus.CANCELLED_BY_MANAGER) {
          return ExtendedTeamRequestStatus.CLIENT_AND_CANCELLED;
        }
        if (status === RequestStatus.SAVED) {
          if (!teamRequest.value.initDate) {
            return ExtendedTeamRequestStatus.CLIENT_AND_SAVED;
          }
          return areAllStemdoersAvailable()
            ? ExtendedTeamRequestStatus.CLIENT_AND_SAVED
            : ExtendedTeamRequestStatus.CLIENT_AND_SAVED_AND_NOT_ALL_STEMDOERS_AVAILABLE;
        }
        if (status === RequestStatus.PENDING) {
          return ExtendedTeamRequestStatus.CLIENT_AND_PENDING;
        }
        if (status === RequestStatus.VALIDATED) {
          return areAllStemdoersAvailable()
            ? modifiedByManager
              ? ExtendedTeamRequestStatus.CLIENT_AND_VALIDATED_AND_MODIFIED_BY_MANAGER
              : ExtendedTeamRequestStatus.CLIENT_AND_VALIDATED_AND_NOT_MODIFIED_BY_MANAGER
            : ExtendedTeamRequestStatus.CLIENT_AND_VALIDATED_AND_NOT_ALL_STEMDOERS_AVAILABLE;
        }
        if (status === RequestStatus.ON_GOING) {
          return ExtendedTeamRequestStatus.CLIENT_AND_ON_GOING;
        }
      }
      return undefined;
    });
    const associatedClientsWithCompany = computed(() => {
      const companyName = teamRequest.value?.client?.company?.name || '';
      return (teamRequest.value.associatedClients || []).map(client => ({
        ...client,
        companyName
      }));
    });
    const showEditTeamRequestBanner = computed(() => {
      const status = teamRequest.value.status;
      return status === RequestStatus.PENDING || status === RequestStatus.VALIDATED;
    });

    onMounted(async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('h')) {
        await retrieveTeamRequest(props.teamRequestId, true);
      } else {
        await retrieveTeamRequest(props.teamRequestId, false);
      }
    });

    const retrieveTeamRequest = (id: string, isHide?: boolean) => {
      if (isHide) {
        teamRequestService
          .find(id, isHide)
          .then(tr => {
            teamRequest.value = new TeamRequest(tr);
            originalStemdoerRates.value = tr.stemdoerRates?.map(sr => Object.assign({}, sr));
            if (!teamRequest.value.areAllStemdoersAvailable() && isUserManager.value === true) {
              existStemdoerNotAvailable.value = !teamRequest.value.areAllStemdoersAvailable() && tr.status === RequestStatus.VALIDATED;
            }
          })
          .catch(error => {
            if (error.response.status === 403) router.push({ name: 'Forbidden' });
            console.error(error);
          });
      } else {
        teamRequestService
          .find(id)
          .then(tr => {
            teamRequest.value = new TeamRequest(tr);
            originalStemdoerRates.value = tr.stemdoerRates?.map(sr => Object.assign({}, sr));
            if (!teamRequest.value.areAllStemdoersAvailable() && isUserManager.value === true) {
              existStemdoerNotAvailable.value = !teamRequest.value.areAllStemdoersAvailable() && tr.status === RequestStatus.VALIDATED;
            }
          })
          .catch(error => {
            if (error.response.status === 403) router.push({ name: 'Forbidden' });
            console.error(error);
          });
      }
    };

    const updateStemdoerRate = (stemdoerId: string, newRate: number) => {
      teamRequest.value.stemdoerRates.find(sr => sr.stemdoer.id === stemdoerId).rate = newRate;
    };

    const refreshTeamRequest = (id?) => {
      if (id) {
        retrieveTeamRequest(id, false);
        return;
      }
      areStemdoersChanged.value = false;
      retrieveTeamRequest(teamRequest.value.id, false);
    };

    const refreshPopUp = () => {
      errorPopUp.value = false;
      successPopUp.value = false;
    };

    const handleSuccess = (message: string) => {
      refreshPopUp();
      successMessage.value = message;
      successPopUp.value = true;
    };

    const handleError = () => {
      refreshPopUp();
      errorPopUp.value = true;
    };

    const handleClosePopUp = () => {
      errorPopUp.value = false;
      successPopUp.value = false;
    };

    const closeAvailableStemdoersModal = () => {
      isAvailableStemdoersModalOpen.value = false;
    };

    const openAvailableStemdoersModal = () => {
      isAvailableStemdoersModalOpen.value = true;
    };

    const handleContinueAction = () => {
      teamRequest.value.removeNotAvailableStemdoers();
      areStemdoersChanged.value = true;
      closeAvailableStemdoersModal();
    };

    const gotoExplore = () => {
      router.push({
        name: 'exploreManager',
        query: { tr: teamRequest.value.id }
      });
    };

    const openPopUp = (open: boolean, text: string) => {
      sPopUpData.value.open = open;
      sPopUpData.value.text = text;
    };

    const getCompanyName = () => {
      if (associatedClientsWithCompany.value.length > 0) {
        return associatedClientsWithCompany.value[0].companyName;
      }
      if (teamRequest.value.client && teamRequest.value.client.company && teamRequest.value.client.company.name) {
        return teamRequest.value.client.company.name;
      }
      return '';
    };

    const getClientList = () => {
      if (associatedClientsWithCompany.value.length > 0) {
        return associatedClientsWithCompany.value;
      }
      if (teamRequest.value.client && teamRequest.value.client.name && teamRequest.value.client.email) {
        return [teamRequest.value.client];
      }
      return [];
    };

    const getCompany = () => {
      if (teamRequest.value.client && teamRequest.value.client['company']) {
        return teamRequest.value.client['company'];
      }
      if (teamRequest.value['company']) {
        return teamRequest.value['company'];
      }
      if (teamRequest.value.associatedClients && teamRequest.value.associatedClients.length > 0) {
        const firstClient = teamRequest.value.associatedClients[0];
        if (firstClient['company']) {
          return firstClient['company'];
        }
      }
      return null;
    };

    return {
      isUserClient,
      isUserManager,
      teamRequest,
      associatedClientsWithCompany,
      extendedTeamRequestStatus,
      isAnyRateChanged,
      areStemdoersChanged,
      isAvailableStemdoersModalOpen,
      existStemdoerNotAvailable,
      errorPopUp,
      successPopUp,
      successMessage,
      sPopUpData,
      showEditTeamRequestBanner,
      openPopUp,
      updateStemdoerRate,
      refreshTeamRequest,
      handleSuccess,
      handleError,
      closeAvailableStemdoersModal,
      handleContinueAction,
      gotoExplore,
      handleClosePopUp,
      getCompanyName,
      getClientList,
      getCompany
    };
  }
});
